import { memo, useCallback } from 'react';
import { Button, Card } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';

import { GROUP_PERMISSIONS } from '../../constants/groupPermissions';
import Can from '../guards/Can';
import MemberAvatarStack from './MemberAvatarStack';

/**
 * One group of the grid: name, description, member avatars with the count, and Manage group.
 * The whole card and the button open the group detail page (there is no preview slide-out).
 *
 * @param {object} props
 * @param {{ id: string, name: string, description: string, memberCount: number,
 *   membersPreview: object[] }} props.group
 * @param {(groupId: string) => void} props.onOpen
 */
const GroupCard = ({ group, onOpen }) => {
  const { t } = useTranslation('people-access');
  const { id, name, description, memberCount, membersPreview } = group;

  const open = useCallback(() => onOpen(id), [onOpen, id]);
  const handleKeyDown = useCallback(
    (event) => {
      if (event.target !== event.currentTarget) return;
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        open();
      }
    },
    [open],
  );
  const handleManage = useCallback(
    (event) => {
      event.stopPropagation();
      open();
    },
    [open],
  );

  return (
    <Card
      data-testid={`group-card-${id}`}
      role="group"
      aria-label={name}
      tabIndex={0}
      onClick={open}
      onKeyDown={handleKeyDown}
      className="flex cursor-pointer flex-col justify-between gap-4 p-4 transition-colors hover:border-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50"
    >
      <div className="flex flex-col gap-1">
        <h3 className="text-sm font-semibold text-content">{name}</h3>
        {description && <p className="line-clamp-2 text-xs text-content-muted">{description}</p>}
      </div>
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <MemberAvatarStack members={membersPreview} total={memberCount} />
          <span className="text-xs text-content-muted">
            {memberCount === 0
              ? t('groups.card.noMembers')
              : t('groups.card.members', { count: memberCount })}
          </span>
        </div>
        <Can permission={GROUP_PERMISSIONS.MANAGE}>
          <Button
            variant="secondary"
            size="sm"
            aria-label={t('groups.card.manageAria', { name })}
            onClick={handleManage}
          >
            {t('groups.card.manage')}
          </Button>
        </Can>
      </div>
    </Card>
  );
};

export default memo(GroupCard);
